import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Flame, BookOpen, BookMarked, Clock, Mic, Clapperboard, Compass } from "lucide-react";
import AppShell from "@/components/AppShell";
import Logo from "@/components/Logo";
import CefrBadge from "@/components/CefrBadge";
import client from "@/lib/api";
import { useLang } from "@/context/LangContext";

const SKILLS = [
  { key: "vocabulary", label: "Vocabulary" }, { key: "grammar", label: "Grammar" },
  { key: "listening", label: "Listening" }, { key: "reading", label: "Reading" },
  { key: "writing", label: "Writing" }, { key: "speaking", label: "Speaking" },
];

export default function Progress() {
  const nav = useNavigate();
  const { t } = useLang();
  const [p, setP] = useState(null);
  useEffect(() => {
    const load = () => client.get("/learn/progress").then(({ data }) => setP(data)).catch(() => setP(null));
    load();
    const onRefresh = () => load();
    const onFocus = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("mel:progress-refresh", onRefresh);
    document.addEventListener("visibilitychange", onFocus);
    return () => { window.removeEventListener("mel:progress-refresh", onRefresh); document.removeEventListener("visibilitychange", onFocus); };
  }, []);
  if (!p) return <AppShell><div className="pt-24 flex justify-center"><Loader2 className="animate-spin text-[#2563EB]" /></div></AppShell>;
  const maxWeek = Math.max(1, ...p.weekly.map((w) => w.minutes));
  const stats = [
    { id: "lessons", icon: BookOpen, label: t("prog_lessons"), val: p.lessons_completed, tint: "#2563EB" },
    { id: "words", icon: BookMarked, label: t("prog_words"), val: p.words, tint: "#3B82F6", to: "/words" },
    { id: "learning", icon: Clock, label: t("prog_learning"), val: `${p.learning_minutes}m`, tint: "#8B5CF6" },
    { id: "speaking", icon: Mic, label: t("prog_speaking"), val: `${p.speaking_minutes}m`, tint: "#2563EB" },
    { id: "stories", icon: Clapperboard, label: t("prog_stories"), val: p.stories, tint: "#3B82F6" },
    { id: "life", icon: Compass, label: t("prog_life"), val: p.life_missions, tint: "#8B5CF6" },
  ];
  return (
    <AppShell>
      <div className="pt-10 pb-2 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2"><Logo size={14} /><span className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#2563EB]">MELLETIC</span></div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-[#0F172A] mt-0.5">{t("prog_title")}</h1>
        </div>
        <span className="flex items-center gap-2 bg-white border border-slate-200 rounded-full pl-2 pr-3 py-1.5"><span className="text-xl">{p.flag}</span><span className="text-sm font-bold text-[#0F172A]">{p.language ? t(`lang_${p.language}`) : p.language_name}</span></span>
      </div>

      <div className="mt-3 rounded-3xl bg-gradient-to-br from-[#2563EB] to-[#3B82F6] p-5 text-white shadow-[0_16px_40px_-20px_rgba(30,86,160,0.9)]">
        <div className="flex items-center justify-between">
          <p className="font-heading text-lg font-extrabold flex items-center gap-2"><CefrBadge level={p.level} size="sm" /> {t("prog_beginner")}</p>
          <p className="font-heading text-2xl font-extrabold">{p.percent}%</p>
        </div>
        <div className="mt-2 h-2.5 rounded-full bg-white/25 overflow-hidden"><div className="h-full bg-white rounded-full" style={{ width: `${p.percent}%` }} /></div>
        <div className="mt-3 flex gap-4 text-sm">
          <span className="flex items-center gap-1"><Flame size={15} /> {t("prog_day_streak", { n: p.streak })}</span>
          <span className="opacity-90">{t("prog_best", { n: p.best_streak })}</span>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3">
        {stats.map((s) => (
          <div key={s.id} onClick={s.to ? () => nav(s.to) : undefined} data-testid={`stat-${s.id}`}
            className={`rounded-2xl bg-white border border-slate-200 p-3 text-center ${s.to ? "cursor-pointer active:bg-slate-50 transition-colors" : ""}`}>
            <span className="w-9 h-9 rounded-xl flex items-center justify-center mx-auto" style={{ background: `${s.tint}18`, color: s.tint }}><s.icon size={17} /></span>
            <p className="font-heading font-extrabold text-[#0F172A] mt-2">{s.val}</p>
            <p className="text-[11px] text-slate-400">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="mt-4 rounded-2xl bg-white border border-slate-200 p-5">
        <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{t("prog_this_week")}</p>
        <div className="mt-3 flex items-end justify-between gap-2 h-24">
          {p.weekly.map((w, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full rounded-t-md bg-[#2563EB]/80" style={{ height: `${(w.minutes / maxWeek) * 80 + 4}px` }} title={`${w.minutes}m`} />
              <span className="text-[10px] text-slate-400">{["S", "M", "T", "W", "T", "F", "S"][new Date(w.date).getDay()]}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 rounded-2xl bg-white border border-slate-200 p-5 mb-4">
        <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{t("prog_skills")}</p>
        <div className="mt-3 space-y-3">
          {SKILLS.map((s) => (
            <div key={s.key} className="flex items-center gap-3">
              <span className="text-sm text-slate-600 w-24 shrink-0">{s.label}</span>
              <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-[#2563EB] rounded-full" style={{ width: `${p.skills[s.key] || 0}%` }} /></div>
              <span className="text-xs font-bold text-slate-500 w-9 text-right">{p.skills[s.key] || 0}%</span>
            </div>
          ))}
        </div>
      </div>
    </AppShell>
  );
}
